import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";

import { UsersListInterface } from "../../../../../redux/interfaces/admin.interface";

import moment from "moment";
moment.locale("ro");

import Dropdown, {
  DropdownValue,
} from "../../../../../components/misc/Dropdown";
import {
  getCurrentYearOrMonth,
  getDaysOfMonthBeforeToday,
  getMonthsBeforeToday,
  monthsDataDropdownValue,
} from "../../../../../utils/dateAndTime";
import useTableCtx from "../../../../../components/tables/tableContext/useTableCtx";
import CustomButton from "../../../../../components/CustomButton";
import {
  getDownloadPickingErrorAdvancedReport,
  getDownloadPickingErrorSimplifiedReport,
} from "../../../../../redux/api/reports.api";
import Text from "../../../../../styleguide/Text";

const lastThreeYears = [
  getCurrentYearOrMonth("year"),
  getCurrentYearOrMonth("year") - 1,
  getCurrentYearOrMonth("year") - 2,
];

const yearsDataDropdownValue = lastThreeYears.map((year: number) => {
  return {
    id: year,
    label: year,
  };
});

const allOption = {
  id: 0,
  label: "Toate",
};

function CollectionErrorsDropdwonsHeader({
  reportType,
}: {
  reportType: "simplified" | "detailed";
}) {
  const dispatch = useAppDispatch();
  const { tableData, handleTableSearch } = useTableCtx() || {};

  const {
    admin: {
      users: {
        data: { data: users },
      },
    },
  } = useAppSelector((state) => ({
    admin: state.admin,
  }));

  const [selectedDay, setSelectedDay] = useState<DropdownValue>(allOption);
  const [selectedMonth, setSelectedMonth] = useState<DropdownValue>(
    monthsDataDropdownValue[getCurrentYearOrMonth("month")],
  );
  const [selectedYear, setSelectedYear] = useState<DropdownValue>(
    yearsDataDropdownValue[0],
  );
  const [selectedUsers, setSelectedUsers] = useState<DropdownValue[]>([]);

  const daysDropdownValue = useMemo(
    () =>
      getDaysOfMonthBeforeToday(selectedMonth.id, selectedYear.id).map(
        (day) => ({ id: parseInt(day), label: day }),
      ),
    [selectedMonth.id, selectedYear.id],
  );

  const daysDropdownValueWithAll = [
    { id: 0, label: "Toate" },
    ...daysDropdownValue,
  ];

  useEffect(() => {
    if (
      Number(selectedDay.id) !== 0 &&
      !daysDropdownValue.some((day) => day.id === Number(selectedDay.id))
    ) {
      setSelectedDay(allOption);
    }
  }, [daysDropdownValue, selectedDay.id]);

  const monthsDropdownValue = getMonthsBeforeToday(selectedYear.id);

  useEffect(() => {
    handleTableSearch && handleTableSearch("selectedDay", selectedDay?.id);
  }, [handleTableSearch, selectedDay]);

  useEffect(() => {
    handleTableSearch && handleTableSearch("selectedMonth", selectedMonth?.id);
  }, [handleTableSearch, selectedMonth]);

  useEffect(() => {
    handleTableSearch && handleTableSearch("selectedYear", selectedYear?.id);
  }, [handleTableSearch, selectedYear]);

  useEffect(() => {
    handleTableSearch &&
      handleTableSearch("selectedUsers", JSON.stringify(selectedUsers));
  }, [handleTableSearch, selectedUsers]);

  const usersDropdownValue = users.map((user: UsersListInterface) => {
    return {
      id: user.userId,
      label: user.userName,
    };
  });

  const isAdvancedReport = reportType === "detailed";

  const exportRaport = async () => {
    if (isAdvancedReport) {
      await dispatch(
        getDownloadPickingErrorAdvancedReport({
          day: selectedDay?.id,
          month: selectedMonth?.id,
          year: selectedYear?.id,
          selectedUsers: selectedUsers,
        }),
      );
    } else {
      await dispatch(
        getDownloadPickingErrorSimplifiedReport({
          day: selectedDay?.id,
          month: selectedMonth?.id,
          year: selectedYear?.id,
          selectedUsers: selectedUsers,
        }),
      );
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <Text
          variant="h3"
          component="h3"
          className="font-semibold text-slate-900 mb-4"
          gutterBottom>
          {isAdvancedReport ? "Raport detaliat" : "Raport simplificat"}
        </Text>
        {tableData && tableData?.length > 0 && (
          <CustomButton
            variant="contained"
            withConfirmationModal={{
              modalTitle: "Exporta raport",
              modalSubtitle: `Esti sigur ca vrei sa exporti raportul ${
                isAdvancedReport ? "avansat" : "simplificat"
              }?`,
              modalLeftButtonText: "Anuleaza",
              modalLeftButtonVariant: "outlined",
              modalRightButtonText: "Exporta",
              modalRightButtonOnClick: exportRaport,
            }}>
            Exporta raport
          </CustomButton>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 pb-4 sm:grid-cols-2 xl:grid-cols-4">
        <Dropdown
          className="!mb-0 min-w-0"
          title="Selecteaza ziua"
          data={daysDropdownValueWithAll}
          singleValue={selectedDay}
          setSingleValue={(value) => value && setSelectedDay(value)}
        />

        <Dropdown
          className="!mb-0 min-w-0"
          title="Selecteaza luna"
          data={monthsDropdownValue}
          singleValue={selectedMonth}
          setSingleValue={(value) => value && setSelectedMonth(value)}
        />

        <Dropdown
          className="!mb-0 min-w-0"
          title="Selecteaza anul"
          data={yearsDataDropdownValue}
          singleValue={selectedYear ?? yearsDataDropdownValue[0]}
          setSingleValue={(value) => value && setSelectedYear(value)}
        />

        <Dropdown
          className="!mb-0 min-w-0"
          title="Username colector"
          data={usersDropdownValue}
          multipleValues={selectedUsers}
          setMultipleValues={setSelectedUsers}
        />
      </div>
    </div>
  );
}

export default CollectionErrorsDropdwonsHeader;
